import type { IndicatorKey } from "@/types/futuresChart"

export type ChartThemeName = "dark" | "light"

/** Visual tokens for canvas charts and their HTML chrome (tooltip, legend, hints). */
export interface ChartTheme {
  name: ChartThemeName
  background: string
  grid: string
  text: string
  textStrong: string
  up: string
  down: string
  line: string
  volume: string
  openInterest: string
  crosshair: string
  crosshairLabelBackground: string
  crosshairLabelText: string
  bandFill: string
  mutedLine: string
  alertLine: string
  drawingLine: string
  suggestionLine: string
  indicators: Record<IndicatorKey, string>
  /**
   * Categorical series hues, assigned in this fixed order and never cycled past the end.
   * Validated with the dataviz palette checker against `background`: adjacent CVD ΔE ≥ 8.4,
   * normal-vision ΔE ≥ 19.3. In light mode slots 3–5 sit under 3:1 contrast, so series
   * always ship with a labelled legend.
   */
  palette: readonly string[]
  /** Tailwind classes for the HTML chrome drawn over the canvas. */
  classes: {
    surface: string
    tooltip: string
    tooltipStrong: string
    hint: string
    divider: string
    card: string
    header: string
    eyebrow: string
    title: string
    muted: string
    value: string
    segmentGroup: string
    segmentOn: string
    segmentOff: string
    toggleOn: string
    toggleOff: string
  }
}

const DARK: ChartTheme = {
  name: "dark",
  background: "#07111f",
  grid: "rgba(148, 163, 184, 0.13)",
  text: "#94a3b8",
  textStrong: "#e2e8f0",
  up: "#2dd4bf",
  down: "#fb7185",
  line: "#60a5fa",
  volume: "rgba(96, 165, 250, 0.42)",
  openInterest: "#c084fc",
  crosshair: "rgba(226, 232, 240, 0.48)",
  crosshairLabelBackground: "#1e293b",
  crosshairLabelText: "#e2e8f0",
  bandFill: "rgba(148, 163, 184, 0.18)",
  mutedLine: "#94a3b8",
  alertLine: "#fb7185",
  drawingLine: "#22d3ee",
  suggestionLine: "#fbbf24",
  indicators: { sma20: "#c98500", sma50: "#9085e9", ema20: "#3987e5" },
  palette: ["#3987e5", "#d95926", "#199e70", "#c98500", "#d55181", "#008300", "#9085e9", "#e66767"],
  classes: {
    surface: "bg-[#07111f]",
    tooltip: "border-white/10 bg-slate-950/80 text-slate-300",
    tooltipStrong: "text-slate-100",
    hint: "text-slate-500",
    divider: "border-white/10",
    card: "border-slate-800 bg-slate-950 shadow-2xl shadow-slate-950/20",
    header: "border-slate-800 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950",
    eyebrow: "text-cyan-400",
    title: "text-white",
    muted: "text-slate-400",
    value: "text-slate-300",
    segmentGroup: "bg-slate-900",
    segmentOn: "bg-slate-700 text-white shadow",
    segmentOff: "text-slate-400 hover:text-slate-200",
    toggleOn: "border-cyan-500/40 bg-cyan-500/10 text-cyan-300",
    toggleOff: "border-slate-700 text-slate-500 hover:text-slate-300",
  },
}

const LIGHT: ChartTheme = {
  name: "light",
  background: "#ffffff",
  grid: "rgba(100, 116, 139, 0.16)",
  text: "#64748b",
  textStrong: "#0f172a",
  up: "#0f8a5f",
  down: "#d63a3a",
  line: "#2a78d6",
  volume: "rgba(42, 120, 214, 0.32)",
  openInterest: "#6250d6",
  crosshair: "rgba(15, 23, 42, 0.4)",
  crosshairLabelBackground: "#0f172a",
  crosshairLabelText: "#f8fafc",
  bandFill: "rgba(100, 116, 139, 0.16)",
  mutedLine: "#64748b",
  alertLine: "#d63a3a",
  drawingLine: "#0e7490",
  suggestionLine: "#b45309",
  indicators: { sma20: "#b45309", sma50: "#6250d6", ema20: "#2a78d6" },
  palette: ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#6250d6", "#e34948"],
  classes: {
    surface: "bg-white",
    tooltip: "border-slate-200 bg-white/90 text-slate-600",
    tooltipStrong: "text-slate-900",
    hint: "text-slate-400",
    divider: "border-slate-200",
    card: "border-slate-200 bg-white shadow-sm",
    header: "border-slate-200 bg-slate-50",
    eyebrow: "text-cyan-700",
    title: "text-slate-900",
    muted: "text-slate-500",
    value: "text-slate-700",
    segmentGroup: "bg-slate-100",
    segmentOn: "bg-white text-slate-900 shadow",
    segmentOff: "text-slate-500 hover:text-slate-800",
    toggleOn: "border-cyan-600/40 bg-cyan-50 text-cyan-800",
    toggleOff: "border-slate-200 text-slate-400 hover:text-slate-700",
  },
}

export const CHART_THEMES: Record<ChartThemeName, ChartTheme> = { dark: DARK, light: LIGHT }

/** Dark tokens; kept for callers that are not theme-aware yet. */
export const CHART_COLORS = DARK

/**
 * Fixed-order categorical color for the series at `index`. Hues are never cycled: series
 * past the palette's end fall back to the muted line colour (and are told apart by label).
 */
export function paletteColor(theme: ChartTheme, index: number): string {
  return theme.palette[index] ?? theme.mutedLine
}
